import { CallDirection } from '../../models/dto/callDirection';
import { MappingRequirementType } from '../../models/mappingRequirementType';
import { PropertiesByValueType } from '../../models/propertiesByValueType';
import {
    CallDetails,
    CreateCall
} from '../../models/dto/call';

export interface CreateJobsState {
    byEngagement: Map<number, CreateJobState>;
}

export interface CreateJobState {
    callLogData: CallLogData;
	prevSteps: CreateJobStep[];
	currentStep: CreateJobStep;
    nextSteps: CreateJobStep[];
    pristineMappingSteps: CreateJobStep[];
    createRequestStatus: CreateJobRequestStatus;
}

interface StepBase {
    id: string;
}

export interface GatherInfoStep extends StepBase {
    type: 'info';
    name: string;
    customFields: CreateJobCustomField[];
}

export interface CreateJobCustomField {
    fieldId: number;
    fieldDisplayName: string;
    value: string;
    defaultValue: string | nundefined;
    required: boolean;
}

export interface ChooseLogFilesStep extends StepBase {
    type: 'logFiles';
    files: ParsedCallLogFile[];
}

interface ParsedCallLogFileBase {
    id: string;
    filename: string;
}

export interface ParsedCallLogFileData {
    rows: string[][];
}

export interface ParsedCallLogCsvFile extends ParsedCallLogFileBase {
    type: 'csv';
    data: ParsedCallLogFileData;
}

export interface ParsedCallLogXlsFile extends ParsedCallLogFileBase {
    type: 'xls';
    selectedSheet: ParsedCallLogXlsFileSheet;
    allSheets: ParsedCallLogXlsFileSheet[];
}

export interface ParsedCallLogXlsFileSheet {
    sheetId: string;
    name: string;
    data: ParsedCallLogFileData;
}

export type ParsedCallLogFile = ParsedCallLogCsvFile | ParsedCallLogXlsFile;

export interface MapCallFieldsStep extends StepBase {
    type: 'mapCallFields';
    mappings: CallFieldMappingConfig[];
}

export type CreateJobCallField = PropertiesByValueType<CreateCall, string | nundefined>;

/**
 * describes where this value will be mapped
 * to on the dto.
 */
export type CallFieldMappingDestination = { type: 'field'; field: CreateJobCallField; } | { type: 'customField'; fieldId: number; };

interface CallFieldMappingConfigBase {
    id: string;
    displayName: string;
    requirementType: MappingRequirementType;
    helpText?: string;
}

export interface BasicCallFieldMappingConfig extends CallFieldMappingConfigBase {
    type: 'basic';
    columnIndex: number | null;
    destination: CallFieldMappingDestination;
    maxLengthConfig: CallFieldMaxLengthConfig | nundefined;
}

export interface DirectionCallFieldMappingConfig extends CallFieldMappingConfigBase {
    type: 'direction';
    modeConfig: JobModeDirectionCallFieldMappingConfig | CallModeDirectionCallFieldMappingConfig;
    destination: CallFieldMappingDestination;
}

export interface JobModeDirectionCallFieldMappingConfig {
    type: 'job'; 
    direction: CallDirection;
}

export interface CallModeDirectionCallFieldMappingConfig {
    type: 'call';
    /**
     * mappings of call log values to
     * Direction values
     */
    mappings: Map<string, CallDirection>;
    columnIndex: number | null;
}

export interface DateCallFieldMappingConfig extends CallFieldMappingConfigBase {
    type: 'date'; 
    timezone: string;
    columnIndex: number | null;
    destination: CallFieldMappingDestination;
}

export interface FilenameCallFieldMappingConfig extends CallFieldMappingConfigBase {
    type: 'filename'; 
    columnIndex: number | null;
    destination: CallFieldMappingDestination;
    maxLengthConfig: CallFieldMaxLengthConfig;
}

export interface SessionIdCallFieldMappingConfig extends CallFieldMappingConfigBase {
    type: 'sessionId'; 
    columnIndex: number | null;
    destination: CallFieldMappingDestination;
    maxLengthConfig: CallFieldMaxLengthConfig;
}

export interface ParticipantNameCallFieldMappingConfig extends CallFieldMappingConfigBase {
    type: 'participantName';
    participantType: ParticipantNameMappingParticipantType;
    modeConfig: SingleFieldModeParticipantNameCallFieldMappingConfig | MultiFieldModeParticipantNameCallFieldMappingConfig;
    firstMaxLengthConfig: CallFieldMaxLengthConfig;
    lastMaxLengthConfig: CallFieldMaxLengthConfig;
}

export type ParticipantNameMappingParticipantType = 'agent' | 'customer';

interface SingleFieldModeParticipantNameCallFieldMappingConfig {
    type: 'singleField';
    format: ParticipantNameMappingFormat;
    columnIndex: number;
}

export enum ParticipantNameMappingFormat {
    FirstSpaceLast = 'FirstSpaceLast',
    LastCommaFirst = 'LastCommaFirst'
}

export interface MultiFieldModeParticipantNameCallFieldMappingConfig {
    type: 'multiField';
    firstNameColumnIndex: number | null;
    lastNameColumnIndex: number | null;
}

export type CallFieldMappingConfig = BasicCallFieldMappingConfig | DirectionCallFieldMappingConfig | DateCallFieldMappingConfig 
    | FilenameCallFieldMappingConfig | SessionIdCallFieldMappingConfig | ParticipantNameCallFieldMappingConfig;

export type CallFieldMappingConfigDescriptor = Omit<BasicCallFieldMappingConfig, 'columnIndex'> | Omit<DirectionCallFieldMappingConfig, 'modeConfig'> 
    | Omit<DateCallFieldMappingConfig, 'columnIndex' | 'timezone'> | Omit<FilenameCallFieldMappingConfig, 'columnIndex'> 
    | Omit<SessionIdCallFieldMappingConfig, 'columnIndex'> | Omit<ParticipantNameCallFieldMappingConfig, 'modeConfig'>;

export interface CallFieldMaxLengthConfig {
    maxLength: number;
}

export type CreateJobStep = GatherInfoStep | ChooseLogFilesStep | MapCallFieldsStep;

export interface CallLogData {
    headers: string[];
    callRows: string[][];
}

export type CreateJobRequestStatus = 'none' | 'creating' | 'finishing' | 'complete';